import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { UI, PATHS } from "@/i18n/ui";
import { translatePath } from "@/i18n/paths";
import { SITE, waLink } from "@/data/site";
import { AREAS } from "@/data/areas";

const LangSwitcher = ({ lang, pathname, altPath }) => (
  <div className="flex items-center text-xs font-semibold border border-[#E5E1D8] rounded-full overflow-hidden" data-testid="language-switcher">
    <Link to={lang === "id" ? pathname : altPath} data-testid="lang-id"
      className={`px-2.5 py-1.5 ${lang === "id" ? "bg-[#4A6B53] text-white" : "text-[#4B5563] hover:text-[#4A6B53]"}`}>ID</Link>
    <Link to={lang === "en" ? pathname : altPath} data-testid="lang-en"
      className={`px-2.5 py-1.5 ${lang === "en" ? "bg-[#4A6B53] text-white" : "text-[#4B5563] hover:text-[#4A6B53]"}`}>EN</Link>
  </div>
);

const MobileMenu = ({ lang, t, p, links, onClose }) => (
  <nav className="xl:hidden bg-white border-t border-[#E5E1D8] px-6 py-4 space-y-1" data-testid="mobile-menu" aria-label="Mobile navigation">
    {links.map(([href, label]) => (
      <Link key={href} to={href} onClick={onClose}
        className="block py-2.5 text-[15px] font-medium text-[#1F2937] border-b border-[#F2EFE9]">
        {label}
      </Link>
    ))}
    <div className="pt-3 flex gap-3">
      <Link to={p.booking} onClick={onClose} data-testid="mobile-book-cta"
        className="flex-1 text-center rounded-full bg-[#4A6B53] text-white text-sm font-medium px-4 py-3">
        {t.cta.book}
      </Link>
      <a href={waLink(lang === "id" ? "Halo Bali Empower, saya ingin bertanya." : "Hello Bali Empower, I would like to ask a question.")}
        target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp"
        className="flex items-center gap-1.5 rounded-full border border-[#4A6B53] text-[#4A6B53] text-sm font-medium px-4 py-3">
        <MessageCircle size={16} /> WA
      </a>
    </div>
  </nav>
);

const buildNavLinks = (t, p) => [
  [p.services, t.nav.services],
  [p.psychologists, t.nav.psychologists],
  [p.assessment, t.nav.assessment],
  [p.programs, t.nav.programs],
  [p.mentalHealth, t.nav.mentalHealth],
  [p.faq, t.nav.faq],
  [p.location, t.nav.location],
  [p.contact, t.nav.contact],
];

const DesktopNav = ({ links, pathname }) => (
  <nav className="hidden xl:flex items-center gap-5" aria-label="Main navigation">
    {links.map(([href, label]) => (
      <Link key={href} to={href} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`be-link text-[13px] font-medium ${pathname === href ? "text-[#4A6B53]" : "text-[#4B5563]"}`}>
        {label}
      </Link>
    ))}
  </nav>
);

export const Header = ({ lang }) => {
  const t = UI[lang];
  const p = PATHS[lang];
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const altPath = translatePath(pathname);
  const links = buildNavLinks(t, p);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-[#E5E1D8]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-16 lg:h-[72px]">
        <Link to={p.home} data-testid="header-logo" className="flex items-baseline gap-1">
          <span className="font-display text-xl lg:text-2xl font-semibold text-[#1F2937]">Bali</span>
          <span className="font-display text-xl lg:text-2xl font-semibold text-[#4A6B53]">Empower</span>
        </Link>

        <DesktopNav links={links} pathname={pathname} />
        <div className="flex items-center gap-3">
          <LangSwitcher lang={lang} pathname={pathname} altPath={altPath} />
          <Link to={p.booking} data-testid="header-book-cta"
            className="hidden md:inline-flex items-center rounded-full bg-[#4A6B53] hover:bg-[#3A5542] text-white text-sm font-medium px-5 py-2.5 transition-colors">
            {t.cta.book}
          </Link>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu"
            className="xl:hidden p-2 text-[#1F2937]">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && <MobileMenu lang={lang} t={t} p={p} links={links} onClose={() => setOpen(false)} />}
    </header>
  );
};

export const StickyCta = ({ lang }) => {
  const t = UI[lang];
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-50 grid grid-cols-2 border-t border-[#E5E1D8] bg-white/95 backdrop-blur-lg" data-testid="sticky-mobile-cta">
      <a href={waLink(lang === "id" ? "Halo Bali Empower, saya ingin berkonsultasi." : "Hello Bali Empower, I would like a consultation.")}
        target="_blank" rel="noopener noreferrer" data-testid="sticky-whatsapp-btn"
        className="flex items-center justify-center gap-2 py-3.5 text-sm font-semibold text-[#4A6B53]">
        <MessageCircle size={17} /> {t.cta.whatsappShort}
      </a>
      <Link to={PATHS[lang].booking} data-testid="sticky-book-btn"
        className="flex items-center justify-center gap-2 py-3.5 text-sm font-semibold bg-[#4A6B53] text-white">
        {t.cta.bookShort}
      </Link>
    </div>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4 font-sans">{title}</h3>
    <ul className="space-y-2 text-sm">
      {links.map(({ to, label, testId }) => (
        <li key={label}>
          <Link className="hover:text-white" to={to} data-testid={testId}>{label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const FooterClinic = ({ lang, t }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4 font-sans">{t.footer.clinic}</h3>
    <ul className="space-y-2 text-sm mb-5">
      {AREAS.map((a) => (
        <li key={a.key}>
          <Link className="hover:text-white" to={lang === "en" ? `/en/${a.slug.en}` : `/${a.slug.id}`} data-testid={`footer-area-${a.key}`}>
            {a.label[lang]}
          </Link>
        </li>
      ))}
    </ul>
    <address className="not-italic text-sm leading-relaxed text-white/60 space-y-2">
      <p>{SITE.legalName}</p>
      <p>{SITE.address.street}<br />{SITE.address.area}, {SITE.address.city}<br />{SITE.address.region} {SITE.address.postal}, {SITE.address.country}</p>
      <p><a href={`tel:${SITE.phoneIntl}`} data-testid="footer-phone" className="hover:text-white">{SITE.phone}</a></p>
      <p>
        <a href={waLink("Halo Bali Empower")} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-white">WhatsApp</a>
        {" · "}
        <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-maps" className="hover:text-white">Google Maps</a>
      </p>
    </address>
  </div>
);

const buildFooterLinks = (t, p, lang) => ({
  services: [
    { to: p.services, label: t.nav.services },
    { to: p.assessment, label: t.nav.assessment },
    { to: p.programs, label: t.nav.programs },
    { to: p.psychologists, label: t.nav.psychologists },
    { to: p.booking, label: t.cta.book },
  ],
  resources: [
    { to: p.mentalHealth, label: t.nav.mentalHealth },
    { to: p.answers, label: t.nav.answers },
    { to: "/en/psychologist-for-expats-bali", label: lang === "en" ? "For Expats in Bali" : "For Expats (English)", testId: "footer-expats-link" },
    { to: p.faq, label: t.nav.faq },
    { to: p.privacy, label: t.footer.privacy },
    { to: p.terms, label: t.footer.terms },
    { to: p.cancellation, label: t.footer.cancellation },
  ],
});

export const Footer = ({ lang }) => {
  const t = UI[lang];
  const p = PATHS[lang];
  const footerLinks = buildFooterLinks(t, p, lang);
  return (
    <footer className="bg-[#1F2937] text-white/80 mt-24 pb-20 md:pb-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-baseline gap-1 mb-4">
            <span className="font-display text-xl font-semibold text-white">Bali</span>
            <span className="font-display text-xl font-semibold text-[#9CBFA6]">Empower</span>
          </div>
          <p className="text-sm leading-relaxed text-white/60">{t.footer.desc}</p>
        </div>
        <FooterCol title={t.footer.services} links={footerLinks.services} />
        <FooterCol title={t.footer.resources} links={footerLinks.resources} />
        <FooterClinic lang={lang} t={t} />
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        © {new Date().getFullYear()} Bali Empower · {SITE.address.area}, {SITE.address.city}, {SITE.address.region}, {SITE.address.country}
      </div>
    </footer>
  );
};
